import React, { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import toast from 'react-hot-toast';
import { Package, Send, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { SkeletonPage } from '../ui/kit';
import { nurDatum } from '../lib/zeit';

const STATUS_LABEL = {
  open: { label: 'Offen', color: '#4b5563', bg: '#f3f4f6' },
  forwarded_to_driver: { label: 'An Fahrer weitergeleitet', color: '#4b5563', bg: '#f3f4f6' },
  resolved: { label: 'Gelöst', color: '#9c27b0', bg: '#f6f0fa' },
};

export default function LostItems() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState({});

  const load = () => {
    setLoading(true); setLoadError(false);
    api.get('/admin/lost-items').then(r => setItems(r.data.items))
      .catch(() => setLoadError(true)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const action = async (id, actionType) => {
    try {
      await api.patch(`/admin/lost-items/${id}`, { action: actionType, admin_note: note[id] || '' });
      toast.success(actionType === 'forward_to_driver' ? 'An Fahrer weitergeleitet' : 'Als gelöst markiert');
      setNote(prev => ({ ...prev, [id]: '' }));
      load();
    } catch { toast.error('Fehler'); }
  };

  const counts = { open: 0, forwarded_to_driver: 0, resolved: 0 };
  items.forEach(i => { if (counts[i.status] !== undefined) counts[i.status]++; });

  if (loading) return <SkeletonPage />;
  if (loadError) return (
    <div style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>
      <PageHeader title="Verlorene Gegenstände" />
      <p role="alert">Meldungen konnten nicht geladen werden. Bitte erneut laden.</p>
      <button type="button" onClick={load}>Erneut laden</button>
    </div>
  );

  return (
    <div style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>
      <PageHeader title="Verlorene Gegenstände" />

      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        {[['open','Offen','#4b5563'],['forwarded_to_driver','Weitergeleitet','#4b5563'],['resolved','Gelöst','#9c27b0']].map(([k, label, color]) => (
          <div key={k} style={{ background: '#f8f9fa', borderRadius: 10, padding: '8px 14px', border: `1px solid ${color}22` }}>
            <span style={{ fontSize: 22, fontWeight: 800, color }}>{counts[k]}</span>
            <span style={{ fontSize: 12, color: '#888', marginLeft: 6 }}>{label}</span>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
          <Package size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div>Keine Meldungen vorhanden</div>
        </div>
      ) : items.map(item => {
        const st = STATUS_LABEL[item.status] || STATUS_LABEL.open;
        const isOpen = expanded === item.id;
        return (
          <div key={item.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #eee', marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer' }} onClick={() => setExpanded(isOpen ? null : item.id)}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Package size={18} color={st.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description}</div>
                <div style={{ fontSize: 12, color: '#888' }}>
                  Kunde: {item.customer_name} · {nurDatum(item.created_at)}
                  {item.driver_name ? ` · Fahrer: ${item.driver_name}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: st.bg, color: st.color, fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 6 }}>{st.label}</span>
                {isOpen ? <ChevronUp size={16} color="#aaa" /> : <ChevronDown size={16} color="#aaa" />}
              </div>
            </div>

            {/* Expanded detail */}
            {isOpen && (
              <div style={{ borderTop: '1px solid #0a0a0a', padding: '16px 18px', background: '#fafafa' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 4 }}>KUNDE</div>
                    <div style={{ fontWeight: 700 }}>{item.customer_name}</div>
                    <div style={{ fontSize: 12, color: '#555' }}>{item.customer_email}</div>
                    {item.customer_phone && <div style={{ fontSize: 12, color: '#555' }}>{item.customer_phone}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 4 }}>FAHRER</div>
                    <div style={{ fontWeight: 700 }}>{item.driver_name || '—'}</div>
                    {item.driver_phone && <div style={{ fontSize: 12, color: '#555' }}>{item.driver_phone}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 4 }}>ABHOLUNG</div>
                    <div style={{ fontSize: 13 }}>{item.pickup_address || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 4 }}>ZIEL</div>
                    <div style={{ fontSize: 13 }}>{item.dropoff_address || '—'}</div>
                  </div>
                </div>

                {item.driver_response && (
                  <div style={{ background: '#f3f4f6', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>FAHRER ANTWORT</div>
                    <div style={{ fontSize: 13, color: '#4b5563' }}>{item.driver_response}</div>
                  </div>
                )}

                {item.admin_note && (
                  <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', marginBottom: 4 }}>ADMIN NOTIZ</div>
                    <div style={{ fontSize: 13 }}>{item.admin_note}</div>
                  </div>
                )}

                {item.status !== 'resolved' && (
                  <div>
                    <textarea
                      placeholder="Notiz an Fahrer / für interne Verwendung..."
                      value={note[item.id] || ''}
                      onChange={e => setNote(prev => ({ ...prev, [item.id]: e.target.value }))}
                      rows={2}
                      style={{ width: '100%', borderRadius: 10, border: '1px solid #ddd', padding: '10px 12px', fontSize: 13, resize: 'none', marginBottom: 10, fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      {item.status === 'open' && item.driver_name && (
                        <button onClick={() => action(item.id, 'forward_to_driver')}
                          style={{ flex: 1, padding: '10px', background: '#4b5563', color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <Send size={14} /> An Fahrer weiterleiten
                        </button>
                      )}
                      <button onClick={() => action(item.id, 'resolve')}
                        style={{ flex: 1, padding: '10px', background: '#9c27b0', color: '#fff', borderRadius: 10, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <CheckCircle size={14} /> Als gelöst markieren
                      </button>
                    </div>
                  </div>
                )}

                {item.status === 'resolved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9c27b0', fontSize: 13, fontWeight: 700 }}>
                    <CheckCircle size={16} /> Gelöst am {nurDatum(item.resolved_at)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
